<script setup lang="ts">
/**
 * AccessibilitySettings — painel de preferências de acessibilidade.
 *
 * Cobre F1-F6 da Proposta Lume (Fase 0). Fica dentro de SettingsState
 * (acessado via link no HomeState footer) e respeita Princípio V
 * (transição sempre opt-in pelo aprendiz).
 *
 * Constitucional:
 * - P-UX-01: tudo opt-in; defaults seguem o OS.
 * - P-UX-04: preferências são legítimas; zero julgamento.
 * - Princípio X: dignidade — sem "modo especial" implícito.
 */
import { storeToRefs } from "pinia";
import { useAccessibilityStore } from "../store";
import type {
  FontSize,
  FontFamily,
  ColorMode,
  ContrastMode,
  ColorblindMode,
  MotionMode,
} from "../types";

interface Emits {
  (e: "close"): void;
}

const emit = defineEmits<Emits>();

const store = useAccessibilityStore();
const { preferences } = storeToRefs(store);

function set<K extends keyof typeof preferences.value>(
  key: K,
  value: (typeof preferences.value)[K],
): void {
  store.setPreferences({ [key]: value });
}

function reset(): void {
  store.resetToDefaults();
}

const fontSizes: { value: FontSize; label: string }[] = [
  { value: "small", label: "Pequena" },
  { value: "medium", label: "Média" },
  { value: "large", label: "Grande" },
  { value: "x-large", label: "Extra grande" },
];

const fontFamilies: { value: FontFamily; label: string }[] = [
  { value: "system", label: "Padrão do sistema" },
  { value: "dyslexic", label: "OpenDyslexic" },
];

const colorModes: { value: ColorMode; label: string }[] = [
  { value: "system", label: "Seguir sistema" },
  { value: "light", label: "Claro" },
  { value: "dark", label: "Escuro" },
];

const contrastModes: { value: ContrastMode; label: string }[] = [
  { value: "normal", label: "Normal" },
  { value: "high", label: "Alto contraste" },
];

const colorblindModes: { value: ColorblindMode; label: string }[] = [
  { value: "none", label: "Nenhum" },
  { value: "protanopia", label: "Protanopia" },
  { value: "deuteranopia", label: "Deuteranopia" },
  { value: "tritanopia", label: "Tritanopia" },
];

const motionModes: { value: MotionMode; label: string }[] = [
  { value: "system", label: "Seguir sistema" },
  { value: "reduced", label: "Reduzido" },
  { value: "full", label: "Completo" },
];
</script>

<template>
  <section
    class="hlos-a11y"
    role="region"
    aria-labelledby="hlos-a11y-title"
  >
    <header class="hlos-a11y__header">
      <h2 id="hlos-a11y-title">Acessibilidade</h2>
      <button
        type="button"
        class="hlos-a11y__close"
        @click="emit('close')"
        aria-label="Fechar configurações de acessibilidade"
      >
        Voltar
      </button>
    </header>

    <p class="hlos-a11y__intro">
      Estas preferências são suas. Você pode mudar a qualquer momento.
      Nenhuma opção é "certa" ou "errada".
    </p>

    <fieldset class="hlos-a11y__group">
      <legend>Tamanho da fonte</legend>
      <div role="radiogroup" aria-label="Tamanho da fonte">
        <label
          v-for="opt in fontSizes"
          :key="opt.value"
          class="hlos-a11y__option"
        >
          <input
            type="radio"
            name="font-size"
            :value="opt.value"
            :checked="preferences.fontSize === opt.value"
            @change="set('fontSize', opt.value)"
          />
          <span>{{ opt.label }}</span>
        </label>
      </div>
    </fieldset>

    <fieldset class="hlos-a11y__group">
      <legend>Fonte</legend>
      <div role="radiogroup" aria-label="Família da fonte">
        <label
          v-for="opt in fontFamilies"
          :key="opt.value"
          class="hlos-a11y__option"
        >
          <input
            type="radio"
            name="font-family"
            :value="opt.value"
            :checked="preferences.fontFamily === opt.value"
            @change="set('fontFamily', opt.value)"
          />
          <span>{{ opt.label }}</span>
        </label>
      </div>
    </fieldset>

    <fieldset class="hlos-a11y__group">
      <legend>Tema de cor</legend>
      <div role="radiogroup" aria-label="Tema de cor">
        <label
          v-for="opt in colorModes"
          :key="opt.value"
          class="hlos-a11y__option"
        >
          <input
            type="radio"
            name="color-mode"
            :value="opt.value"
            :checked="preferences.colorMode === opt.value"
            @change="set('colorMode', opt.value)"
          />
          <span>{{ opt.label }}</span>
        </label>
      </div>
    </fieldset>

    <fieldset class="hlos-a11y__group">
      <legend>Contraste</legend>
      <div role="radiogroup" aria-label="Contraste">
        <label
          v-for="opt in contrastModes"
          :key="opt.value"
          class="hlos-a11y__option"
        >
          <input
            type="radio"
            name="contrast"
            :value="opt.value"
            :checked="preferences.contrastMode === opt.value"
            @change="set('contrastMode', opt.value)"
          />
          <span>{{ opt.label }}</span>
        </label>
      </div>
    </fieldset>

    <fieldset class="hlos-a11y__group">
      <legend>Modo para daltonismo</legend>
      <div role="radiogroup" aria-label="Modo para daltonismo">
        <label
          v-for="opt in colorblindModes"
          :key="opt.value"
          class="hlos-a11y__option"
        >
          <input
            type="radio"
            name="colorblind"
            :value="opt.value"
            :checked="preferences.colorblindMode === opt.value"
            @change="set('colorblindMode', opt.value)"
          />
          <span>{{ opt.label }}</span>
        </label>
      </div>
    </fieldset>

    <fieldset class="hlos-a11y__group">
      <legend>Movimento</legend>
      <div role="radiogroup" aria-label="Movimento">
        <label
          v-for="opt in motionModes"
          :key="opt.value"
          class="hlos-a11y__option"
        >
          <input
            type="radio"
            name="motion"
            :value="opt.value"
            :checked="preferences.motionMode === opt.value"
            @change="set('motionMode', opt.value)"
          />
          <span>{{ opt.label }}</span>
        </label>
      </div>
    </fieldset>

    <fieldset class="hlos-a11y__group">
      <legend>Modo calmo</legend>
      <label class="hlos-a11y__option hlos-a11y__option--inline">
        <input
          type="checkbox"
          :checked="preferences.slowMode === 'on'"
          @change="
            set('slowMode', ($event.target as HTMLInputElement).checked ? 'on' : 'off')
          "
        />
        <span>
          Mais tempo para transições e respostas. Sem pressa.
        </span>
      </label>
    </fieldset>

    <fieldset class="hlos-a11y__group">
      <legend>Voz</legend>
      <label class="hlos-a11y__option hlos-a11y__option--inline">
        <input
          type="checkbox"
          :checked="preferences.voiceInputEnabled"
          @change="
            set(
              'voiceInputEnabled',
              ($event.target as HTMLInputElement).checked,
            )
          "
        />
        <span>Permitir entrada por voz (microfone)</span>
      </label>
      <label class="hlos-a11y__option hlos-a11y__option--inline">
        <input
          type="checkbox"
          :checked="preferences.voiceOutputEnabled"
          @change="
            set(
              'voiceOutputEnabled',
              ($event.target as HTMLInputElement).checked,
            )
          "
        />
        <span>Permitir leitura em voz alta</span>
      </label>
      <div v-if="preferences.voiceOutputEnabled" class="hlos-a11y__slider">
        <label for="voice-rate">Velocidade da voz: {{ preferences.voiceRate.toFixed(1) }}×</label>
        <input
          id="voice-rate"
          type="range"
          min="0.5"
          max="2"
          step="0.1"
          :value="preferences.voiceRate"
          @input="
            set(
              'voiceRate',
              Number(($event.target as HTMLInputElement).value),
            )
          "
        />
      </div>
    </fieldset>

    <div class="hlos-a11y__actions">
      <button
        type="button"
        class="hlos-a11y__reset"
        @click="reset"
        aria-label="Restaurar preferências padrão"
      >
        Restaurar padrões
      </button>
    </div>
  </section>
</template>

<style scoped>
.hlos-a11y {
  max-width: 720px;
  margin: 0 auto;
  padding: 1.5rem;
}
.hlos-a11y__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}
.hlos-a11y__header h2 {
  margin: 0;
  font-size: 1.25rem;
}
.hlos-a11y__close {
  background: var(--hlos-color-hover);
  border: 1px solid var(--hlos-color-neutral-200);
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
}
.hlos-a11y__intro {
  color: var(--hlos-color-neutral-600);
  margin: 0 0 1.5rem;
}
.hlos-a11y__group {
  border: 1px solid var(--hlos-color-neutral-200);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin: 0 0 1rem;
}
.hlos-a11y__group legend {
  font-weight: 600;
  padding: 0 0.5rem;
}
.hlos-a11y__option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0;
  cursor: pointer;
}
.hlos-a11y__option--inline {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}
.hlos-a11y__slider {
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.hlos-a11y__actions {
  display: flex;
  justify-content: flex-end;
}
.hlos-a11y__reset {
  background: transparent;
  border: 1px solid var(--hlos-color-neutral-300);
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  color: var(--hlos-color-neutral-700);
}
.hlos-a11y__reset:hover,
.hlos-a11y__reset:focus-visible {
  background: var(--hlos-color-hover);
}
</style>
